import { Button, Card, Empty, notification, Spin, Switch, Tooltip } from 'antd';
import { OverViewCardInfo } from '../OverviewCardInfo';
import AddTextingWorkflowForm from './AddTextingWorkflowForm';
import EditTextingWorkflowForm from './EditTextingWorkflowForm';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { DeleteOutline, EditOutlined, DifferenceOutlined } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import {
	QueryObserverResult,
	RefetchOptions,
	useMutation,
	UseMutationResult,
} from '@tanstack/react-query';
import { MODAL_IDS } from 'src/enums/EModal';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal, closeModal } from '@/store/modal/slice';
import './TextingWorkflowInfo.scss';
import { deepEqual, safeJSONParse } from '@/utils/helper';
import { User } from '@/types/TAccounts';
import { TUTORIALS } from 'src/enums/ETutorials';
import { generateSignedUrl } from '@/api/user';
import { SetupWorkflowAvailability } from '../../../../../components/WorkflowAvailability/WorkflowAvailability';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { fetchAndUpdateWorkflowAnalysis } from '@/utils/analysisWorkflow';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import { decrementScenarioCount, incrementScenarioCount } from '@/store/scenarioCounts/slice';
import { WorkflowType } from 'src/enums/EWorkflow';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';
import { InfoCircleOutlined, LoadingOutlined, SettingOutlined } from '@ant-design/icons';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';

interface TextingWorkflowInfoProps {
	data: any;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	debrand: boolean;
	isNoviUser: boolean;
}

export const TextingWorkflowInfo: React.FC<TextingWorkflowInfoProps> = ({
	data,
	refetch,
	mutation,
	debrand,
	isNoviUser,
}) => {
	const dispatch = useAppDispatch();

	const [textingWorkflowData, setTextingWorkflowData] = useState<any[]>([]);
	const [scenarioIndex, setScenarioIndex] = useState<number>();
	const [editFormInitialValues, setEditFormInitialValues] = useState<any>(null);
	const [addFormInitialValues, setAddFormInitialValues] = useState<any>(null);
	const [duplicateWorkflow, setDuplicateWorkflow] = useState<any>(null);
	const canAddScenario = useAppSelector(selectCanAddScenario);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const [textingWorkflow, setTextingWorkflow] = useState<any>(null);
	const isWorkflowAvailabilityFeatureEnabled = useFeatureFlag(FeatureFlag.WORKFLOW_AVAILABILITY);

	const signedURLMutation = useMutation({
		mutationKey: ['generateSignedUrl'],
		mutationFn: generateSignedUrl,
	});

	useEffect(() => {
		const fetch = async () => {
			if (data?.workflows) {
				try {
					const res = safeJSONParse(data?.workflows, []);
					const updatedData = await Promise.all(
						res.map(async (item: any) => {
							let presignedUrl;
							if (item?.imageS3Key) {
								const { url } = await signedURLMutation.mutateAsync({
									bucketType: 'textingwf',
									objectKey: item?.imageS3Key,
								});
								presignedUrl = url;
							}

							return {
								...item,
								presignedUrl,
							};
						}),
					);

					setTextingWorkflowData(updatedData);
				} catch (error) {
					console.error('Error parsing workflows data:', error);
					notification.error({
						message: 'Something went wrong, please try again later!',
					});
				}
			}
		};

		fetch();
	}, [data?.workflows]);

	useEffect(() => {
		const addInitialValues = {
			workflows: [
				{
					previousWorkflowData: textingWorkflowData,
				},
			],
		};

		if (
			!deepEqual(addFormInitialValues?.workflows?.[0]?.previousWorkflowData, textingWorkflowData)
		) {
			setAddFormInitialValues(addInitialValues);
		}
	}, [textingWorkflowData]);

	const handleEditScenario = async (scenario: any, index: number) => {
		let freshPresignedUrl = scenario.presignedUrl;
		if (scenario.imageS3Key) {
			try {
				const { url } = await signedURLMutation.mutateAsync({
					bucketType: 'textingwf',
					objectKey: scenario.imageS3Key,
				});
				freshPresignedUrl = url;
			} catch (error) {
				console.error('Error generating signed URL:', error);
				freshPresignedUrl = scenario.presignedUrl;
			}
		}

		const editInitialValues = {
			workflows: [
				{
					id: scenario.id,
					description: scenario.description,
					voiceResponse: scenario.voiceResponse,
					textMessage: scenario.textMessage,
					shortURLEnabled: scenario.shortURLEnabled,
					askForPermissionOnText: scenario.askForPermissionOnText,
					customTextFooter: scenario.customTextFooter,
					imageS3Key: scenario.imageS3Key,
					presignedUrl: freshPresignedUrl, // Use the fresh URL
					postExecutionPrompt: scenario.postExecutionPrompt,
					configEnabled: scenario.configEnabled ?? false,
					fallbackMessage: scenario.fallbackMessage || '',
					availability: scenario.availability || null,
					previousWorkflowData: textingWorkflowData,
				},
			],
		};

		setEditFormInitialValues(editInitialValues);
		setScenarioIndex(index);
		dispatch(openModal({ modalId: MODAL_IDS.TEXTING_WORKFLOW_EDIT_MODAL }));
	};

	const handleDuplicateScenario = (index: number) => {
		const workflowToDuplicate = textingWorkflowData[index];
		workflowToDuplicate.id = undefined;
		setDuplicateWorkflow(workflowToDuplicate);
		dispatch(openModal({ modalId: MODAL_IDS.TEXTING_WORKFLOW_DUPLICATE_MODAL }));
	};

	const handleDuplicateModalClose = () => {
		setDuplicateWorkflow(null);
		dispatch(closeModal());
	};

	const handleDeleteScenario = async (index: number) => {
		try {
			dispatch(decrementScenarioCount(WorkflowType.TEXT));

			await mutation.mutateAsync({
				workflows: textingWorkflowData?.filter((_, i) => i !== index),
				deleteIndex: index,
				agentId: selectedAgentId as string,
			});
			notification.success({ message: `Scenario ${index + 1} deleted successfully.` });
			refetch();
			await fetchAndUpdateWorkflowAnalysis(dispatch, selectedAgentId);
		} catch {
			dispatch(incrementScenarioCount(WorkflowType.TEXT));
			notification.error({ message: 'Something went wrong, please try again later!' });
		}
	};

	const handleAvailability = (index: number) => {
		setScenarioIndex(index);
		const val = textingWorkflowData[index];
		setTextingWorkflow(val);
		dispatch(openModal({ modalId: MODAL_IDS.TEXTING_WORKFLOW_AVAILABILITY_MODAL }));
	};

	const handleSetWorkflowAvailability = async (
		data: TWorkflowAvailabilities,
		index: number,
		fallbackMessage: string,
		configEnabled: boolean,
	) => {
		try {
			const currentWorkflows = textingWorkflowData || [];
			currentWorkflows[index].availability = data;

			currentWorkflows[index] = {
				...currentWorkflows[index],
				availability: data,
				configEnabled,
				fallbackMessage,
			};

			if (!selectedAgentId) {
				throw new Error('Agent ID is required');
			}
			await mutation.mutateAsync({
				workflows: currentWorkflows,
				agentId: selectedAgentId,
			});
			await refetch();
			notification.success({
				message: 'Workflow Availability has been updated!',
				duration: 3,
			});
		} catch (error: any) {
			notification.error({
				message: 'Error updating availability',
				description: error.toString(),
			});
		}
	};

	const handleDuplicateButtonClick = (index: number) => {
		if (!canAddScenario) {
			notification.error({
				message: 'Scenario limit reached',
				description: 'Max scenarios (100) reached. Please Delete existing to add more.',
			});
			return;
		}
		handleDuplicateScenario(index);
	};

	const handleConfirmDelete = (index: number) => {
		ConfirmDeleteModal({
			itemName: `Scenario ${index + 1}`,
			onConfirm: () => handleDeleteScenario(index),
		});
	};

	const InfoComponent = () => {
		return (
			<>
				{textingWorkflowData && textingWorkflowData.length > 0 ? (
					textingWorkflowData.map((textingWorkflow, index) => (
						<Card
							key={`${textingWorkflow.id}-${index}`}
							className="mt-4"
							title={
								<div className={`${baseClass}_button-container`}>
									<span className="font-bold text-base">{`Scenario ${index + 1}`}</span>
									<div className="flex items-center gap-2">
										<Tooltip title="Duplicate This Scenario">
											<Button
												type="default"
												icon={<DifferenceOutlined />}
												onClick={() => handleDuplicateButtonClick(index)}
												data-feature-gate-id={'duplicate-text-workflow-btn'}
											/>
										</Tooltip>
										<Button
											loading={mutation.isPending && mutation.variables.deleteIndex === index}
											type="primary"
											danger
											icon={<DeleteOutline />}
											onClick={() => handleConfirmDelete(index)}
											data-testid={`delete-button-${index}`}
										>
											<div>Delete</div>
										</Button>
										<Button
											type="primary"
											icon={<EditOutlined />}
											onClick={() => handleEditScenario(textingWorkflow, index)}
											data-testid={`edit-button-${index}`}
										>
											<div>Edit</div>
										</Button>
										{isWorkflowAvailabilityFeatureEnabled && (
											<Button
												type="primary"
												icon={<SettingOutlined />}
												onClick={() => handleAvailability(index)}
											>
												<div>Availability</div>
											</Button>
										)}
									</div>
								</div>
							}
						>
							<>
								<div className="border-b">
									<LabelWithDescription
										customClassName="mt-2"
										label="Enable Short URLs"
										description="Enable this option to shorten the URLs in the text message. This will allow you to track the analytics of the URL in the Analytics section."
									/>
									<Switch
										checked={textingWorkflow?.shortURLEnabled}
										disabled
										className=" mt-2 mb-4"
									/>
								</div>
								<div className="border-b">
									<LabelWithDescription
										customClassName="mt-2"
										label="Scenario Description"
										description="Describe the scenario in which you would like to send the caller a text message. The AI is intelligent enough to know when the scenario is triggered!"
									/>
									<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">{textingWorkflow.description}</span>
								</div>
								<div className="border-b">
									<LabelWithDescription
										customClassName="mt-2"
										label="Text Message"
										description="Specify the text you want your AI receptionist to send when the scenario is triggered."
									/>
									<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">{textingWorkflow.textMessage}</span>
								</div>
								{textingWorkflow.postExecutionPrompt && (
									<div>
										<LabelWithDescription
											customClassName="mt-2"
											label="What should the AI do next? "
											description="Specify instructions for the AI to follow after it executes this workflow."
										/>
										<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">
											{textingWorkflow.postExecutionPrompt}
										</span>
									</div>
								)}
								{debrand || isNoviUser ? (
									<div>
										<LabelWithDescription
											customClassName="mt-2"
											label="Custom Text Footer"
											description="Add a custom text footer to your text message."
										/>
										<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">
											{textingWorkflow.customTextFooter}
										</span>
									</div>
								) : null}
								{/* Show image section only if this scenario has an image */}
								{textingWorkflow.imageS3Key && textingWorkflow.presignedUrl && (
									<div className="border-b">
										<LabelWithDescription
											customClassName="mt-2"
											label="Attached Image"
											description="Image attached to this text message."
										/>
										<div className="pl-4 w-8/12 mt-2 pt-2 pb-4">
											<img
												src={textingWorkflow.presignedUrl}
												alt="Workflow attachment"
												style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
												onError={(e) => {
													e.currentTarget.style.display = 'none';
												}}
											/>
										</div>
									</div>
								)}
								<div>
									<LabelWithDescription
										customClassName="mt-2"
										label="Ask before sending Text SMS"
										description="If checked in, above message will be sent when the caller confirms for the text sms"
									/>
									<span className="pl-4 w-8/12 mt-2 pt-2 pb-4">
										{textingWorkflow.askForPermissionOnText ? 'Yes' : 'No'}
									</span>
								</div>
							</>
						</Card>
					))
				) : (
					<Empty />
				)}
			</>
		);
	};

	const baseClass = 'texting-workflow';
	return (
		<div className={baseClass}>
			<OverViewCardInfo
				width={'50%'}
				formActionType="Add"
				form={
					<AddTextingWorkflowForm
						mutation={mutation}
						initialValues={addFormInitialValues}
						refetch={refetch}
						debrand={debrand}
						isNoviUser={isNoviUser}
					/>
				}
				formTitle="Add Texting Workflow"
				info={<InfoComponent />}
				footer={null}
				modalId={MODAL_IDS.TEXTING_WORKFLOW_MODAL}
				tutorialId={TUTORIALS.TEXTING_WORKFLOW}
				displayTutorialActionBtn={!debrand}
				addButtonFeatureGateId={'add-text-workflow-btn'}
			/>

			<CustomModal
				width={'50%'}
				title={<span className="font-bold">Edit Scenario</span>}
				children={
					<EditTextingWorkflowForm
						initialValues={editFormInitialValues}
						refetch={refetch}
						mutation={mutation}
						scenarioIndex={scenarioIndex ?? 0}
						debrand={debrand}
						isNoviUser={isNoviUser}
					/>
				}
				modalId={MODAL_IDS.TEXTING_WORKFLOW_EDIT_MODAL}
				footer={null}
			/>
			<CustomModal
				width={'50%'}
				title={<span className="font-bold">Duplicate Scenario</span>}
				children={
					<AddTextingWorkflowForm
						mutation={mutation}
						initialValues={
							duplicateWorkflow
								? {
										workflows: [
											{ ...duplicateWorkflow, previousWorkflowData: textingWorkflowData },
										],
									}
								: undefined
						}
						refetch={refetch}
						debrand={debrand}
						isNoviUser={isNoviUser}
						isDuplicate={true}
						showAddFieldButton={false}
					/>
				}
				modalId={MODAL_IDS.TEXTING_WORKFLOW_DUPLICATE_MODAL}
				footer={null}
				cancelAction={handleDuplicateModalClose}
				destroyOnClose={true}
			/>
			{isWorkflowAvailabilityFeatureEnabled && (
				<CustomModal
					width={'50%'}
					title={
						<span className="font-bold">
							Setup WorkFlow Availability
							<Tooltip
								title="Set your AI workflow's operating hours. Choose business hours, weekends, or customize it to match your ideal schedule."
								className="mx-1"
							>
								<InfoCircleOutlined
									style={{ fontSize: '10px' }}
									className="text-gray-400 cursor-help"
								/>
							</Tooltip>
						</span>
					}
					children={
						<Spin
							spinning={mutation.isPending}
							indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
						>
							<SetupWorkflowAvailability
								mutation={handleSetWorkflowAvailability}
								workflowData={textingWorkflow}
								scenarioIndex={scenarioIndex}
							/>
						</Spin>
					}
					modalId={MODAL_IDS.TEXTING_WORKFLOW_AVAILABILITY_MODAL}
					footer={null}
					destroyOnClose={true}
				/>
			)}
		</div>
	);
};
